import { useState } from 'react';
import { X, ChevronDown, ChevronRight } from 'lucide-react';

const SECTIONS = [
  {
    title: 'Getting Started',
    items: [
      { label: 'Create a room', desc: 'Enter a room name and tap "Create Room". Share the 6-character code with your friends via iMessage, Snapchat, or whatever you use.' },
      { label: 'Join a room', desc: 'Enter the room code your friend shared with you. Codes are 6 characters, letters and numbers.' },
      { label: 'Nickname', desc: 'Pick a name so your friends know who\'s drawing. It\'s saved for next time.' },
      { label: 'Rejoin', desc: 'If you were in a room recently, you\'ll see a quick "Rejoin" option on the home screen.' },
    ],
  },
  {
    title: 'Drawing Tools',
    items: [
      { label: '✏️ Pencil', desc: 'Thin, slightly textured line. Great for sketching and detail work.' },
      { label: '🖌️ Brush', desc: 'Wide strokes with tapered ends. Feels like a real paintbrush.' },
      { label: '🖍️ Marker', desc: 'Flat, even strokes like a felt-tip marker. No pressure variation.' },
      { label: '🖊️ Pen', desc: 'Fine, smooth line. Perfect for clean outlines and lettering.' },
      { label: '🧹 Eraser', desc: 'Removes parts of your drawing. Works on all strokes, not just yours.' },
    ],
  },
  {
    title: 'Shapes & Extras',
    items: [
      { label: '▭ Rectangle', desc: 'Click and drag to draw a rectangle. Hold for a square.' },
      { label: '◯ Ellipse', desc: 'Click and drag to draw an oval or circle.' },
      { label: '╱ Line', desc: 'Click and drag to draw a straight line.' },
      { label: '〰️ Bezier Curve', desc: 'Click to place control points for a smooth curve. Double-click to finish the curve.' },
      { label: '🪣 Fill Bucket', desc: 'Click on an area to flood-fill it with your selected colour.' },
      { label: '🎟️ Stamps', desc: 'Pick an emoji sticker and click to place it on the canvas. Use the size slider to make them bigger or smaller.' },
    ],
  },
  {
    title: 'Advanced Tools',
    items: [
      { label: '🔤 Text', desc: 'Click anywhere on the canvas to place a text box. Type your text and press Enter to commit. Font size follows the size slider.' },
      { label: '👆 Select / Move', desc: 'Click on any stroke, shape, or text to select it (highlighted in amber). Drag to reposition it on the canvas.' },
      { label: '💧 Eyedropper', desc: 'Click anywhere on the canvas to sample the colour at that point. The sampled colour becomes your active drawing colour.' },
      { label: '🎨 Gradient Fill', desc: 'Click and drag to create a gradient-filled rectangle. Uses your primary and secondary colours. The secondary colour picker appears in the toolbar when this tool is active.' },
      { label: '🔄 Rotate / Transform', desc: 'Click on a stroke to select it, then drag to rotate and scale. Drag further from the centre to make it larger.' },
      { label: '✂️ Clipping Mask', desc: 'Click on a shape (rectangle or ellipse) to set it as a clipping boundary. While active, your freehand strokes are tagged to that region. Click again to remove.' },
    ],
  },
  {
    title: 'Colour & Size',
    items: [
      { label: 'Colour palette', desc: '20 art-grade colours in the toolbar. Tap any swatch to use it.' },
      { label: 'Custom colour', desc: 'Tap the rainbow circle at the bottom of the palette to pick any colour.' },
      { label: 'Secondary colour', desc: 'When the gradient tool is active, a second colour picker appears. Use the swap button (⇅) to exchange primary and secondary colours.' },
      { label: 'Size slider', desc: 'Drag the vertical slider or use the quick-pick buttons (XS, S, M, L, XL) to change stroke width.' },
      { label: 'Opacity', desc: 'Slide to make your strokes more transparent (10%) or fully solid (100%).' },
    ],
  },
  {
    title: 'Canvas Controls',
    items: [
      { label: 'Zoom', desc: 'Scroll wheel to zoom in/out. Use the +/− buttons in the bottom-left corner. Click the percentage to reset.' },
      { label: 'Pan', desc: 'Hold Spacebar and drag to move around the canvas. Or use middle mouse button.' },
      { label: 'Undo', desc: 'Cmd+Z (Mac) or Ctrl+Z (Windows) to undo your last stroke. Only undoes your own strokes.' },
      { label: 'Export PNG', desc: 'Tap the download icon to save the canvas as a high-res PNG image.' },
    ],
  },
  {
    title: 'Collaboration',
    items: [
      { label: 'Live cursors', desc: 'You can see where everyone else is drawing in real-time — their cursor shows their name and active tool.' },
      { label: 'Members panel', desc: 'Tap the people icon to see who\'s in the room.' },
      { label: 'Room code', desc: 'Tap the code badge in the top bar to copy the invite link.' },
      { label: 'Switch rooms', desc: 'Tap the swap icon (⇄) next to the room code to jump to a different room without leaving. Enter a code or pick from your recent rooms. Great for hopping between games.' },
    ],
  },
  {
    title: 'Voice Chat',
    items: [
      { label: 'How it works', desc: 'DrawLab voice is a drop-in voice channel, like Discord. Tap the phone icon in the bottom-right corner to join the voice channel for your room. Anyone else who taps the phone icon will join too — and you\'ll hear each other immediately.' },
      { label: 'No ringing, no waiting', desc: 'You don\'t "call" a specific person. Voice is a shared space that people step into when they want to talk. Think of it like walking into the workshop and starting a conversation.' },
      { label: 'Joining and leaving', desc: 'Tap the phone to join — your browser will ask for microphone permission the first time. Tap the red phone button to leave voice. Your drawing continues uninterrupted either way.' },
      { label: 'Who\'s in voice', desc: 'Once you\'re in voice, a small panel shows everyone else who has also joined. Their avatar pulses amber when they\'re speaking.' },
      { label: 'Mute yourself', desc: 'Tap the microphone button next to the hang-up button to mute. Useful if the room is noisy or someone else is talking and you\'re just listening.' },
      { label: 'Privacy', desc: 'Voice is peer-to-peer — your audio goes directly to other participants\' devices, never through a server. DrawLab never records anything you say.' },
    ],
  },
  {
    title: 'Layers & Solo Mode',
    items: [
      { label: 'Foreground / Background', desc: 'Use the FG/BG buttons in the toolbar to choose which layer you\'re drawing on. Background strokes appear behind foreground strokes.' },
      { label: 'Solo Mode', desc: 'Tap the person icon in the toolbar to enter Solo Mode. Your strokes are private — only you can see them. When you\'re happy, tap the share button to push them to the room.' },
    ],
  },
  {
    title: 'Save, Start Over & Export',
    items: [
      { label: 'Save to Gallery', desc: 'Tap the image icon in the top bar to open the Gallery, then "Save Snapshot" to capture the current canvas. Snapshots are shared with everyone in the room — anyone can download them later.' },
      { label: 'Export as PNG', desc: 'Tap the download icon to save the current canvas as a high-resolution PNG to your device. This is just for you — others don\'t see it.' },
      { label: 'Start a new canvas (host only)', desc: 'Open Host Controls (crown icon) and tap "Start New Canvas". You\'ll be offered the option to save a snapshot to the gallery first. Then tap again to confirm. The canvas clears for everyone in the room.' },
      { label: 'What happens during a game', desc: 'When the host starts a game, the canvas is automatically cleared for everyone so the game begins fresh. Your pre-game canvas is remembered and comes back when the game ends — no need to save it first.' },
      { label: 'Delete a snapshot', desc: 'In the Gallery, hover or tap on any snapshot you own (or any snapshot if you\'re the host) to see a delete button. Once deleted, snapshots are gone from the shared gallery.' },
      { label: 'Replay & Export Video', desc: 'Tap the play icon for a stroke-by-stroke replay of the session. In the replay panel, tap "Export Video" to record the replay as a downloadable video file.' },
    ],
  },
  {
    title: 'Host Controls',
    items: [
      { label: 'Who is the host?', desc: 'The person who created the room is the host. If they leave, the person who joined earliest becomes the new host.' },
      { label: 'Crown icon', desc: 'Only the host sees the crown icon in the top bar. Tap it to open host controls.' },
      { label: 'Lock room', desc: 'Prevent new people from joining. Unlock to let them in again.' },
      { label: 'Start New Canvas', desc: 'Clears all drawings from the canvas so you can start fresh. Everyone sees the blank canvas. You can save the current work to the gallery first if you want to keep it.' },
      { label: 'Remove someone', desc: 'Kick a user from the room if needed.' },
      { label: 'Canvas background', desc: 'Switch between Paper, White, Grey, Dark, and Black backgrounds.' },
    ],
  },
  {
    title: 'Game Modes',
    items: [
      { label: 'Starting a game', desc: 'The host opens Host Controls (crown icon) and taps "Start a Game". Pick either Challenge Mode or Relay Mode. Everyone in the room joins automatically — no separate invite needed.' },
      { label: '🏆 Challenge Mode', desc: 'Everyone draws the same thing on their own private canvas. A prompt is shown (e.g. "Draw your best tiger"). When the timer runs out, all drawings are revealed and everyone votes. Highest score wins!' },
      { label: 'Voting', desc: 'In Challenge Mode, each drawing is shown one at a time in a random order. You can\'t vote on your own. Small groups (3-5 people) rate 1-10 on a slider. Larger groups (6+) use quick 1-3 star taps.' },
      { label: 'Draw-Off', desc: 'If two or more players tie for first place, they go head-to-head in a 60-second draw-off. Everyone else watches and votes again. If it\'s still a tie, they share the win.' },
      { label: '🔄 Relay Mode', desc: 'One shared canvas, but players take turns. Each person gets 10, 20, or 30 seconds to draw before the pen passes to the next player. Watch the drawing emerge as everyone adds to it.' },
      { label: 'Spectating in Relay', desc: 'While someone else is drawing in Relay Mode, you can see their strokes appear live. Tap the emoji bar at the bottom to react — your emojis float up on everyone\'s screen.' },
      { label: 'Blind Relay', desc: 'An optional mode where the drawer can only see the edges of previous work — not the centre. The full picture is revealed at the end. Turns a collaborative drawing into a surprise!' },
      { label: 'Relay Playback', desc: 'After a relay game finishes, tap "Watch it Build" to replay the canvas turn by turn. Use the scrubber to step through each player\'s contribution.' },
      { label: 'Themes & Prompts', desc: 'Challenge Mode has five categories of drawing prompts (Animals, Food, Feelings, Scenes, The Impossible Round). Relay Mode has an optional theme to seed the first player\'s direction — but nobody has to follow it.' },
      { label: 'Ending a game', desc: 'The host can end a game at any time from the results screen or via Host Controls. The room returns to free-draw mode and any previous canvas work is restored.' },
    ],
  },
  {
    title: 'Art Styles',
    items: [
      { label: 'What are Art Styles?', desc: 'DrawLab includes six art styles from across history — Surrealism, Impressionism, Pop Art, Ukiyo-e, Art Nouveau and Expressionism. Each is a "preset" that gives you the right tool, brush size and a restricted palette to draw the way the masters did.' },
      { label: 'Opening Art Styles', desc: 'Tap the book icon in the top bar to open the Art Styles explorer. You can browse all six styles, read about their history, see the key artists, and apply a preset to your canvas.' },
      { label: 'Applying a preset', desc: 'When you apply a style preset, DrawLab switches your tool (e.g. pen for Surrealism, brush for Impressionism), sets the right size and opacity, and restricts your palette to just the colours of that style. It\'s a way to try drawing in a specific tradition without getting lost.' },
      { label: 'Clearing a style', desc: 'When a style is active you\'ll see a coloured badge at the top of your canvas. Tap the X to clear it and return to the full 20-colour palette and all tools.' },
      { label: 'The technique tips', desc: 'Each style has tips for how to draw in that tradition — e.g. "don\'t use outlines" for Impressionism, "think like a billboard" for Pop Art. Follow them as prompts for how to approach your piece.' },
      { label: 'Dig Deeper', desc: 'Every style has links to major museums and online collections. If a style sparks your interest, tap the links to explore works at the Met, Tate, Musée d\'Orsay and more.' },
    ],
  },
  {
    title: 'The Workshop Tradition',
    items: [
      { label: 'What it is', desc: 'Art has always been collaborative. For most of human history, art was made in workshops — shared studios where masters, apprentices and friends worked side by side. DrawLab continues that tradition, reimagined for the digital age.' },
      { label: 'Verrocchio\'s Bottega (Florence, 1460s)', desc: 'Andrea del Verrocchio\'s Florentine workshop trained a young Leonardo da Vinci alongside Perugino and Lorenzo di Credi. Apprentices worked on the same canvases as the master.' },
      { label: 'Raphael\'s Workshop (Rome, 1510s)', desc: 'Raphael ran one of the largest workshops of the High Renaissance with dozens of assistants — including Giulio Romano and Giovanni da Udine — all contributing to the Vatican Stanze.' },
      { label: 'Rubens\' Studio (Antwerp, 1600s)', desc: 'Peter Paul Rubens ran a studio estimated to have produced over 1,400 paintings. Specialists painted different elements — landscapes, animals, drapery — with Rubens orchestrating.' },
      { label: 'The Café Guerbois (Paris, late 1860s)', desc: 'Monet, Renoir, Degas, Manet and Pissarro gathered at this Paris café on Thursday evenings, debating and shaping Impressionism together.' },
      { label: 'The Bauhaus (Weimar, 1919)', desc: 'Walter Gropius founded the Bauhaus in April 1919, reviving the workshop tradition for the 20th century. Students and masters worked together on crafts, design and art.' },
      { label: 'The Utopia Homelands (NT, Australia, 1977-today)', desc: 'The women of Utopia paint side by side in shared studios, developing styles collectively. The late Gloria Petyarre\'s Bush Medicine Leaves technique won the Wynne Prize in 1999 and continues through the women of her family.' },
      { label: 'Warhol\'s Factory (New York, from 1964)', desc: 'Andy Warhol\'s studio, literally called "The Factory", was a collaborative space where assistants mass-produced silkscreen prints — a modern echo of the Renaissance bottega.' },
      { label: 'DrawLab', desc: 'The modern bottega. A digital workshop where friends gather, talk, and create together. Verrocchio would recognise what you\'re doing — he just wouldn\'t believe the tools.' },
    ],
  },
  {
    title: 'Keyboard Shortcuts',
    items: [
      { label: 'Cmd/Ctrl + Z', desc: 'Undo your last stroke' },
      { label: 'Spacebar + drag', desc: 'Pan around the canvas' },
      { label: 'Scroll wheel', desc: 'Zoom in and out' },
    ],
  },
];

function HelpSection({ section }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors"
      >
        <span className="text-sm font-semibold text-charcoal font-display">{section.title}</span>
        {open ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-3 space-y-2">
          {section.items.map(({ label, desc }) => (
            <div key={label}>
              <p className="text-sm font-medium text-charcoal font-display">{label}</p>
              <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HelpPanel({ onClose }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl mx-4 overflow-hidden max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-charcoal font-display">How to Use DrawLab</h2>
            <p className="text-xs text-slate-400 font-display">Everything you need to know</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Sections */}
        <div className="flex-1 overflow-y-auto">
          {SECTIONS.map((section) => (
            <HelpSection key={section.title} section={section} />
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 shrink-0">
          <p className="text-[11px] text-slate-400 font-display text-center">
            DrawLab by SILVER WOLF Productions
          </p>
        </div>
      </div>
    </div>
  );
}
